// ship-to-country-sidebar.js
// ------------------------------
// A self-contained “Ship To Country” sidebar dropdown.
//   • Toggle + caret icon
//   • Country / State / City selects
//   • “Apply Changes” button
//   • Fetch + cache logic
//   • Outside-click to close
// ------------------------------

console.log('✅ ship-to-country-sidebar.js loaded successfully!');

import { APP_CONFIG } from '../../js/config.js';
import { appState } from '../../js/state.js';
import { loadTextTranslations } from '../../js/translator.js';
import { registerEventListenerToAttach } from '../../js/to-attach-event-listener-registry.js';
import { addListenerViaCurrentlyAttachedListenersRegistry } from '../../js/currently-attached-listener-registry.js';
import {
  saveToLocalStorage,
  loadFromLocalStorage,
  saveToLocalStorageWithLimitAndExpiry,
} from '../../js/utils-local-storage.js';

import {
    fetchCountries,
    fetchStates,
    fetchCities
} from '../../services/data-services/country-state-city-data-service.js';

let _tempTextTranslator = null;

export async function initTempTextTranslator(lang) {
  _tempTextTranslator = await loadTextTranslations(lang);
}

/**
 * Builds the “Ship To Country” toggle + dropdown for the sidebar
 */
export async function buildComponentShipToCountrySidebar() {
  console.log(`🔄 Building Component: ShipToCountrySidebar - lang: ${appState.SiteLanguageCookie}`);
  try {
    // 1️⃣ Load translations
    await initTempTextTranslator(appState.SiteLanguageCookie);

    const {
      keySelectShipTo: lblSelectShipTo,
      keyShipToCountry: lblShipToCountry,
      keyStateOrRegion: lblStateOrRegion,
      keyCityOrTown: lblCityOrTown,
      keyApplyChanges: lblApplyChanges,
    } = _tempTextTranslator;

    // 2️⃣ Read current from appState (fallbacks)
    const currCode2 = appState.SiteShipToCountryCodeTwoCookie || 'gb';
    const currCode3 = appState.SiteShipToCountryCodeThreeCookie || 'GBR';

    // 3️⃣ Build HTML
    const html = `
   
      <div id="btnSideBarNavDropdown-btn-select-ship-to" class="SideBarNavDropdown-btn-select-ship-to" data-dropdown-id="shipToSidebarDropdown">
        ${lblSelectShipTo}
      
      <svg fill="#555" height="20px" width="20px" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" class="NewNavSidePanelDropdownFa-caret-down">
      <path d="M13.708 6.29A1.006 1.006 0 0 0 13 6H5.005A1 1 0 0 0 4.3 7.71l4 4a1.013 1.013 0 0 0 1.42 0l4-4a1.01 1.01 0 0 0-.013-1.42z"></path>
    </svg>
      </div>


      <div class="NewNavSidePanelDropdown-container" style="display:none;">

          <div class="divSideBarShipToCountryWrapper">
             <br /> <br />
              <label>${lblShipToCountry}</label><br/>
              <select id="DropDownSiteShipToCountryMobile" style="width:100%"></select>
              <br/><br/>
              <label>${lblStateOrRegion}</label><br/>
              <select id="DropDownSiteShipToStateMobile" style="width:100%"></select>
              <br/><br/>
              <label>${lblCityOrTown}</label><br/>
              <select id="DropDownSiteShipToCityMobile" style="width:100%"></select>
              <br/><br/>
              <button id="btnApplyShipToCountryMobile" class="my-basic-btn my-basic-width-100 my-basic-padding-10">
                ${lblApplyChanges}
              </button>
         </div>
      </div>

  
    `;

    console.log('✅ ShipToCountrySidebar built successfully.');
    // 4️⃣ Defer event wiring until after insertion
    registerEventListenerToAttach(async () => initbuildComponentShipToCountrySidebar());
    return html;
  } catch (err) {
    console.error('❌ Error building ShipToCountrySidebar:', err);
    return `<div class="error">Error loading Ship-To selector</div>`;
  }
}


export async function initbuildComponentShipToCountrySidebar() {
  try {
    await setupGeneralListeners();

    console.log('✅ ShipToCountrySidebar listeners attached.');
  } catch (err) {
    console.error('❌ Error attaching ShipToCountrySidebar listeners:', err);
  }
}

async function setupGeneralListeners() {
  const countryDropdown = document.querySelector('#DropDownSiteShipToCountryMobile');
  const stateDropdown = document.querySelector('#DropDownSiteShipToStateMobile');
  const cityDropdown = document.querySelector('#DropDownSiteShipToCityMobile');

  let isDropdownOpen = false;

  const button = document.getElementById('btnSideBarNavDropdown-btn-select-ship-to');
  if (!button) return;

  const onShipToToggleClick = async () => {
    button.classList.toggle('NewNavSidePanelDropdownActive');
    const dropdownContent = button.nextElementSibling;

    isDropdownOpen = !isDropdownOpen;
    dropdownContent.style.display = isDropdownOpen ? 'block' : 'none';

    if (isDropdownOpen) {
      console.log('🌐 Populating countries on dropdown open');
      await populateCountryDropdown();

      countryDropdown.value = appState?.SiteShipToCountryIDCookie;
      await updateStateDropdown(countryDropdown.value);

      stateDropdown.value = appState?.SiteShipToStateIDCookie;
      await updateCityDropdown(stateDropdown.value);

      cityDropdown.value = appState?.SiteShipToCityCookie;

      console.log('✅ States and cities loaded based on selected country');
    }
  };

  button.removeEventListener('click', onShipToToggleClick);
  button.addEventListener('click', onShipToToggleClick);

  const btnApply = document.querySelector('#btnApplyShipToCountryMobile');
  if (btnApply) {
    const onApplyClick = async () => {
      await applyChanges();
    };
    btnApply.removeEventListener('click', onApplyClick);
    btnApply.addEventListener('click', onApplyClick);
  }

  if (countryDropdown) {
    const onCountryChange = async event => {
      const selectedCountryId = event.target.value;
      console.log('Selected country ID:', selectedCountryId);
      await updateStateDropdown(selectedCountryId);
      const selectedStateId = stateDropdown?.value;
      await updateCityDropdown(selectedStateId);
    };
    countryDropdown.removeEventListener('change', onCountryChange);
    countryDropdown.addEventListener('change', onCountryChange);
  }

  if (stateDropdown) {
    const onStateChange = async event => {
      const selectedStateId = event.target.value;
      console.log('Selected state ID:', selectedStateId);
      await updateCityDropdown(selectedStateId);
    };
    stateDropdown.removeEventListener('change', onStateChange);
    stateDropdown.addEventListener('change', onStateChange);
  }
}



async function populateCountryDropdown() {
  console.log('Populating Select Ship To Country Dropdown Mobile');

  const dropdown = document.querySelector('#DropDownSiteShipToCountryMobile');
  const storageKey = `countries_${appState.SiteLanguageCookie}`;

  // Try to load from localStorage first
  const cachedCountries = loadFromLocalStorage(storageKey);

  // Check if we have valid cached data
  if (cachedCountries && Array.isArray(cachedCountries) && cachedCountries.length > 0) {
    console.log('✅ Loaded countries from cache');

    await renderCountryOptions(dropdown, cachedCountries);

    return;
  }

  // Fetch from the server if not cached or empty
  console.log('🌐 Fetching countries from server');

  const countries = await fetchCountries();
  if (countries.length === 0) return;

  await renderCountryOptions(dropdown, countries);
}

async function renderCountryOptions(dropdown, countries) {
  //  console.log(`🌐 Rendering country options for language: ${lang}`);

  // const lang = appState?.SiteLanguageCookie || "en";

  //// ✅ Fetch all translations in parallel
  //const [
  //    textkeySelectCountry,

  //] = await Promise.all([
  //    getTextTranslationByKey("keySelectCountry", lang),

  //]);

  const { keySelectCountry: textkeySelectCountry } = _tempTextTranslator;

  dropdown.innerHTML = `<option value="0">${textkeySelectCountry}</option>`;
  countries.forEach(country => {
    const option = document.createElement('option');
    option.value = country.countryID;
    option.textContent = country.name;

    // Add data attributes for code and code3
    option.dataset.code = country.countryCode.toLowerCase(); // Assuming 'code' is the 2-letter code
    option.dataset.code3 = country.countryCode3; // Assuming 'code3' is the 3-letter code
    option.dataset.name = country.name;

    dropdown.appendChild(option);
  });
}

async function updateStateDropdown(countryId) {
  console.log('Updating state dropdown for country ID:', countryId);

  // const lang = appState?.SiteLanguageCookie || "en";

  // // ✅ Fetch all translations in parallel
  //const [
  //    textkeySelectStateOrRegion,

  //] = await Promise.all([
  //    getTextTranslationByKey("keySelectStateOrRegion", lang),

  //]);

  const { keySelectStateOrRegion: textkeySelectStateOrRegion } = _tempTextTranslator;

  console.log('🌐 Fetching states from server for country ID:', countryId);

  const dropdown = document.querySelector('#DropDownSiteShipToStateMobile');
  dropdown.innerHTML = `<option value="0">${textkeySelectStateOrRegion}</option>`;

  const fetchPromise = await fetchStates(countryId);

  // Show spinner if loading takes more than 1 second
  let spinnerIsVisible = false;
  const delayPromise = new Promise(resolve => {
    setTimeout(() => {
      spinnerIsVisible = true;
      showSpinner('.currency-spinner');
      resolve();
    }, 1000);
  });

  // Apply the delay specifically in this method
  // const delayPromise = new Promise((resolve) => setTimeout(resolve, 1000));
  //const showSpinnerPromise = delayPromise.then(() => showSpinner(".state-spinner"));

  try {
    // Wait for both fetch and spinner delay
    await Promise.all([fetchPromise, delayPromise]);

    // Start both the spinner delay and the API call simultaneously
    // const states = await Promise.race([fetchStates(countryId), showSpinnerPromise]);

    const states = fetchPromise;

    states.forEach(state => {
      const option = document.createElement('option');
      option.value = state.stateID;
      option.textContent = state.stateName;
      dropdown.appendChild(option);
    });

    console.log('🌐 Fetched states from server for country ID:', countryId);
  } catch (error) {
    console.error('❌ Error loading states:', error);
  } finally {
    // Hide the spinner after loading is complete
    if (spinnerIsVisible) {
      hideSpinner('.state-spinner');
    }
  }
}

async function updateCityDropdown(stateId) {
  const lang = appState?.SiteLanguageCookie || 'en';

  //// ✅ Fetch all translations in parallel
  //const [
  //    textkeySelectCityOrTown,

  //] = await Promise.all([
  //    getTextTranslationByKey("keySelectCityOrTown", lang),

  //]);

  const { keySelectCityOrTown: textkeySelectCityOrTown } = _tempTextTranslator;

  const dropdown = document.querySelector('#DropDownSiteShipToCityMobile');
  dropdown.innerHTML = `<option value="0">${textkeySelectCityOrTown}</option>`;

  console.log('🌐 Fetching cities from server for state ID:', stateId);

  const fetchPromise = await fetchCities(stateId);

  // Show spinner if loading takes more than 1 second
  let spinnerIsVisible = false;
  const delayPromise = new Promise(resolve => {
    setTimeout(() => {
      spinnerIsVisible = true;
      showSpinner('.currency-spinner');
      resolve();
    }, 1000);
  });

  try {
    // Wait for both fetch and spinner delay
    await Promise.all([fetchPromise, delayPromise]);

    // Start both the spinner delay and the API call simultaneously
    const cities = fetchPromise;

    cities.forEach(city => {
      const option = document.createElement('option');
      option.value = city.cityID;
      option.textContent = city.cityName;
      dropdown.appendChild(option);
    });

    console.log('🌐 Fetched cities from server for state ID:', stateId);
  } catch (error) {
    console.error('❌ Error loading cities:', error);
  } finally {
    // Hide the spinner after loading is complete
    if (spinnerIsVisible) {
      hideSpinner('.city-spinner');
    }
  }
}

function showSpinner(spinnerClass) {
  const spinner = document.querySelector(spinnerClass);
  if (spinner) spinner.style.display = 'inline-block';
}

function hideSpinner(spinnerClass) {
  const spinner = document.querySelector(spinnerClass);
  if (spinner) spinner.style.display = 'none';
}

async function applyChanges() {
  //alert("applyChanges mobile called");

  const btnApply = document.querySelector('#btnApplyShipToCountryMobile');

  // Add the loading class
  btnApply.classList.add('myloading');

  const countryDropdown = document.querySelector('#DropDownSiteShipToCountryMobile');
  const stateDropdown = document.querySelector('#DropDownSiteShipToStateMobile');
  const cityDropdown = document.querySelector('#DropDownSiteShipToCityMobile');

  const countryId = countryDropdown?.value || '237';
  const stateId = stateDropdown?.value || '0';
  const cityId = cityDropdown?.value || '0';

  // Get selected country details from the dropdown's data attributes
  const selectedOption = countryDropdown?.selectedOptions[0];
  const selectedCountryCode = selectedOption?.dataset.code || 'gb';
  const selectedCountryCode3 = selectedOption?.dataset.code3 || 'GBR';
  const selectedCountryName = selectedOption?.dataset.name || 'United Kingdom';

  // Update the displayed country code and flag image
  const countryCodeDiv = document.querySelector('.ship-to-code');
  const countryFlagImg = document.querySelector('#imgSiteShipToCountry');

  // doing the image first since it seesms to take longer to reflect
  if (countryFlagImg) {
    const imgSrcForFlags = `${APP_CONFIG.IMAGES_BASE_URL}imgs/flags/`;
    countryFlagImg.src = `${imgSrcForFlags}${selectedCountryCode.toLowerCase()}.png`;
    countryFlagImg.alt = selectedCountryName;
  }

  if (countryCodeDiv) {
    countryCodeDiv.textContent = selectedCountryCode3;
  }

  //update the app state
  appState.SiteShipToCountryIDCookie = countryId;
  appState.SiteShipToStateIDCookie = stateId;
  appState.SiteShipToCityCookie = cityId;
  appState.SiteShipToCountryCodeTwoCookie = selectedCountryCode;
  appState.SiteShipToCountryCodeThreeCookie = selectedCountryCode3;
  appState.SiteShipToCountryNameCookie = selectedCountryName;

  // Save the selected values to localStorage

  saveToLocalStorage('SiteShipToCountryIDCookie', countryId);
  saveToLocalStorage('SiteShipToStateIDCookie', stateId);
  saveToLocalStorage('SiteShipToCityCookie', cityId);
  saveToLocalStorage('SiteShipToCountryCodeTwoCookie', selectedCountryCode);
  saveToLocalStorage('SiteShipToCountryCodeThreeCookie', selectedCountryCode3);
  saveToLocalStorage('SiteShipToCountryNameCookie', selectedCountryName);

  // console.log(`✅ Applied and saved to localStorage:`, shipToData);

  // Optionally, refresh the page to apply changes
  //setTimeout(() => {
  //    window.location.reload();
  //}, 1000);

  // Remove the loading class after the method completes or 1 second (whichever is longer)
  setTimeout(() => {
    // closeDropdown();

    btnApply.classList.remove('myloading');
  }, 2000);
}


